// The three settings pages: who you are, how you prove it, what you are told.
//
// NESTED UNDER SETTINGS IN THE RAIL RATHER THAN DRAWN AS A STRIP ACROSS THE
// PAGE, which is RegistrationNav's and KioskNav's arrangement and is the one
// the design pack shows: a section's faces sit under its heading. A second
// horizontal strip under the top bar would be a third row of navigation on a
// screen that already has two, and on a narrow viewport it is the row that
// wraps.
//
// THE ORDER IS THE DESIGN'S AND IT IS ALSO AN ARGUMENT. Account first because
// it is the page that answers "who am I signed in as", which is the question
// somebody opens this section to settle. Sign-in and security second because it
// is where the answer is changed. What you are told about last because it is
// the only one of the three that is about the future rather than about now.

import type { ReactNode } from "react";

/**
 * Which of the three is open.
 *
 * Not a `SettingsPanel` in route.ts, because these are three routes rather than
 * three panels of one — route.ts argues why. This type is the nav's own way of
 * saying which sibling it is drawn beside, and it is deliberately not the
 * route's `kind`: `enrol` is the right name for a route that a refusal links to
 * and the wrong name for a tab a person reads.
 */
export type SettingsPage = "account" | "signin" | "notices";

export const SETTINGS_NAV_ITEMS: readonly {
  readonly page: SettingsPage;
  readonly label: string;
}[] = [
  { page: "account", label: "Account" },
  { page: "signin", label: "Sign-in security" },
  { page: "notices", label: "Notifications" },
];

export type SettingsNavProps = {
  readonly page: SettingsPage;
  readonly onOpen: (page: SettingsPage) => void;
  readonly className?: string;
};

export function SettingsNav({ page, onOpen, className }: SettingsNavProps): ReactNode {
  return (
    <nav className={className === undefined ? "sub" : className} aria-label="Settings pages">
      {SETTINGS_NAV_ITEMS.map((item) => (
        <button
          key={item.page}
          type="button"
          aria-current={item.page === page ? "page" : undefined}
          onClick={() => onOpen(item.page)}
        >
          {item.label}
        </button>
      ))}
    </nav>
  );
}
